import React, { useEffect, useMemo, useState } from "react";
import { Search, Loader2, Link2, Unlink, AlertTriangle, CheckCircle2, Download, RefreshCw, ExternalLink } from "lucide-react";
import { adminApi } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const WINDOWS = [7, 28, 90, 180, 365];
const DIMENSIONS = [
  { key: "page", label: "Pages" },
  { key: "query", label: "Search queries" },
  { key: "device", label: "Devices" },
  { key: "country", label: "Countries" },
];

const pct = (v) => `${(v * 100).toFixed(2)}%`;
const num = (v) => Math.round(v).toLocaleString("en-GB");

const Delta = ({ value }) => {
  if (!value) return <span className="text-slate-400">—</span>;
  const up = value > 0;
  return (
    <span className={up ? "text-emerald-600 font-semibold" : "text-red-600 font-semibold"}>
      {up ? "+" : ""}{num(value)}
    </span>
  );
};

const ConnectCard = ({ status, properties, onConnect, onDisconnect, onPick, loading }) => {
  const redirectUri = `${window.location.origin}/api/gsc/oauth/callback`;
  if (!status) return null;

  if (!status.configured) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-5" data-testid="gsc-not-configured">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={18} />
          <div className="text-sm text-amber-900">
            <p className="font-bold">Google OAuth keys aren't set up on the server yet.</p>
            <p className="mt-1">Search Console reuses the same Google Cloud project as your Google Business Profile connection. Add <code className="bg-white/70 px-1 rounded">GBP_CLIENT_ID</code> and <code className="bg-white/70 px-1 rounded">GBP_CLIENT_SECRET</code> and this screen will come to life.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!status.connected) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="gsc-connect-card">
        <h3 className="font-display font-extrabold text-navy text-lg">Connect Google Search Console</h3>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Read-only. CASS only requests the <code className="bg-slate-100 px-1 rounded">webmasters.readonly</code> scope — nothing here can change your site or your Search Console settings.
        </p>
        <ol className="mt-4 text-sm text-slate-600 space-y-2 list-decimal pl-5">
          <li>
            Enable the Search Console API for the same Google Cloud project you used for Google Business Profile:{" "}
            <a href="https://console.cloud.google.com/apis/library/searchconsole.googleapis.com" target="_blank" rel="noopener noreferrer"
              className="font-bold text-orange hover:underline" data-testid="gsc-enable-api-link">
              open the API page <ExternalLink size={12} className="inline" />
            </a>{" "}
            and click <b>Enable</b>.
          </li>
          <li>
            Add this redirect URI to that project's OAuth client (<a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="font-bold text-orange hover:underline">APIs &amp; Services → Credentials</a>, click your OAuth 2.0 Client ID, then <b>Authorised redirect URIs → Add URI</b>):
            <code className="block mt-1.5 bg-slate-100 px-2 py-1.5 rounded break-all text-xs">{redirectUri}</code>
          </li>
          <li>
            Check <a href="https://console.cloud.google.com/apis/credentials/consent" target="_blank" rel="noopener noreferrer" className="font-bold text-orange hover:underline">OAuth consent screen</a> shows <b>Publishing status: In production</b>. If it says <b>Testing</b>, Google expires the connection after 7 days — click <b>Publish app</b> to avoid reconnecting every week.
          </li>
          <li>Click Connect below and sign in with the Google account that owns the Search Console property. Then choose the property.</li>
        </ol>
        <p className="text-xs text-slate-500 mt-3">
          If the property is a <b>Domain</b> property it will appear as <code className="bg-slate-100 px-1 rounded">sc-domain:cassonline.co.uk</code>; a URL-prefix property appears as the full https address. Either works.
        </p>
        <Button onClick={onConnect} className="mt-5 bg-navy hover:bg-orange" data-testid="gsc-connect-btn">
          <Link2 size={15} className="mr-2" /> Connect Search Console
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5" data-testid="gsc-connected-card">
      <div className="flex flex-wrap items-center gap-3">
        <CheckCircle2 className="text-emerald-600" size={18} />
        <span className="text-sm font-bold text-emerald-900">Connected (read-only)</span>
        <span className="text-xs text-emerald-800">Data available up to {status.data_available_to}</span>
        <Button variant="outline" size="sm" onClick={onDisconnect} className="ml-auto" data-testid="gsc-disconnect-btn">
          <Unlink size={14} className="mr-1.5" /> Disconnect
        </Button>
      </div>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[320px]">
          <label className="block text-xs font-bold text-emerald-900 mb-1.5">Property to report on</label>
          <Select value={status.property || ""} onValueChange={onPick}>
            <SelectTrigger className="bg-white" data-testid="gsc-property-select">
              <SelectValue placeholder={loading ? "Loading properties…" : "Choose a property"} />
            </SelectTrigger>
            <SelectContent>
              {(properties || []).map((p) => (
                <SelectItem key={p.site_url} value={p.site_url}>{p.site_url} · {p.permission}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {!status.property && <p className="text-xs text-emerald-800 pb-2">Pick the property, then the reports below will load.</p>}
      </div>
    </div>
  );
};

const UrlInspector = ({ enabled }) => {
  const { toast } = useToast();
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = () => {
    if (!url.trim()) return;
    setBusy(true);
    adminApi.gsc.inspect(url.trim())
      .then(setResult)
      .catch((e) => toast({ title: "Inspection failed", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" }))
      .finally(() => setBusy(false));
  };

  const rows = result ? [
    ["Verdict", result.verdict],
    ["Coverage", result.coverage_state],
    ["Indexing allowed", result.indexing_state],
    ["robots.txt", result.robots_txt_state],
    ["Page fetch", result.page_fetch_state],
    ["Last crawled", result.last_crawl_time],
    ["Google-selected canonical", result.google_canonical],
    ["Your declared canonical", result.user_canonical],
    ["In sitemap", (result.sitemaps || []).join(", ")],
  ] : [];

  const mismatch = result && result.google_canonical && result.user_canonical && result.google_canonical !== result.user_canonical;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="gsc-inspector">
      <h3 className="font-display font-extrabold text-navy text-lg">URL inspection</h3>
      <p className="text-sm text-slate-500 mt-1">Shows Google's <b>indexed</b> version of a URL — not a live crawl. Limited to 2,000 checks a day, so results are cached for a day.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://cassonline.co.uk/accountants-tamworth"
          className="max-w-xl" disabled={!enabled} data-testid="gsc-inspect-input" />
        <Button onClick={run} disabled={!enabled || busy} className="bg-navy hover:bg-orange" data-testid="gsc-inspect-btn">
          {busy ? <Loader2 size={15} className="animate-spin mr-2" /> : <Search size={15} className="mr-2" />} Inspect
        </Button>
      </div>
      {result && (
        <div className="mt-5" data-testid="gsc-inspect-result">
          {mismatch && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <b>Canonical mismatch.</b> Google has chosen <code className="bg-white/70 px-1 rounded break-all">{result.google_canonical}</code> instead of the canonical this page declares.
            </div>
          )}
          <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            {rows.filter(([, v]) => v).map(([k, v]) => (
              <div key={k} className="flex gap-2 border-b border-slate-100 py-1.5">
                <dt className="text-slate-500 w-52 shrink-0">{k}</dt>
                <dd className="font-medium text-navy break-all">{v}</dd>
              </div>
            ))}
          </dl>
          {result.inspection_link && (
            <a href={result.inspection_link} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-orange hover:underline">
              Open in Search Console <ExternalLink size={14} />
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default function SearchConsoleManager() {
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loadingProps, setLoadingProps] = useState(false);
  const [days, setDays] = useState(90);
  const [dimension, setDimension] = useState("page");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");

  const refreshStatus = () => adminApi.gsc.status().then(setStatus).catch(() => setStatus(null));

  useEffect(() => {
    refreshStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get("gsc") === "connected") toast({ title: "Search Console connected", description: "Now choose which property to report on." });
    if (params.get("gsc") === "error") toast({ title: "Couldn't connect to Google", description: "Check the Search Console API is enabled and the redirect URI matches.", variant: "destructive" });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!status?.connected) return;
    setLoadingProps(true);
    adminApi.gsc.properties()
      .then((r) => { setProperties(r.properties || []); if (r.error) toast({ title: "Couldn't list properties", description: r.error, variant: "destructive" }); })
      .catch(() => setProperties([]))
      .finally(() => setLoadingProps(false));
  }, [status?.connected]); // eslint-disable-line

  useEffect(() => {
    if (!status?.connected || !status?.property) { setData(null); return; }
    setLoading(true);
    adminApi.gsc.performance(days, dimension)
      .then(setData)
      .catch((e) => { setData(null); toast({ title: "Couldn't load Search Console data", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" }); })
      .finally(() => setLoading(false));
  }, [status?.connected, status?.property, days, dimension]); // eslint-disable-line

  const pick = (site_url) => adminApi.gsc.setProperty(site_url).then(refreshStatus);
  const connect = () => { window.location.href = adminApi.gsc.startUrl(); };
  const disconnect = () => adminApi.gsc.disconnect().then(() => { setData(null); setProperties([]); refreshStatus(); });

  const rows = useMemo(() => {
    if (!data) return [];
    const f = filter.trim().toLowerCase();
    return f ? data.rows.filter((r) => r.key.toLowerCase().includes(f)) : data.rows;
  }, [data, filter]);

  const exportCsv = () => {
    if (!data) return;
    const head = [dimension, "clicks", "impressions", "ctr", "avg_position", "prev_clicks", "prev_impressions", "delta_clicks", "delta_impressions"];
    const lines = [head.join(",")].concat(rows.map((r) => [
      `"${r.key.replace(/"/g, '""')}"`, r.clicks, r.impressions, r.ctr, r.position, r.prev_clicks, r.prev_impressions, r.delta_clicks, r.delta_impressions,
    ].join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `search-console-${dimension}-${days}d-${data.period.end}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6" data-testid="gsc-manager">
      <ConnectCard status={status} properties={properties} loading={loadingProps}
        onConnect={connect} onDisconnect={disconnect} onPick={pick} />

      {status?.connected && status?.property && (
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex gap-1.5" data-testid="gsc-window-toggle">
              {WINDOWS.map((w) => (
                <button key={w} onClick={() => setDays(w)} data-testid={`gsc-window-${w}`}
                  className={`text-xs font-bold px-3 py-2 rounded-full border transition-colors ${days === w ? "bg-navy text-white border-navy" : "bg-white text-navy border-slate-200 hover:border-orange"}`}>
                  {w === 365 ? "12 months" : `${w} days`}
                </button>
              ))}
            </div>
            <div className="flex gap-1.5 ml-2" data-testid="gsc-dimension-toggle">
              {DIMENSIONS.map((d) => (
                <button key={d.key} onClick={() => setDimension(d.key)} data-testid={`gsc-dim-${d.key}`}
                  className={`text-xs font-bold px-3 py-2 rounded-full border transition-colors ${dimension === d.key ? "bg-orange text-white border-orange" : "bg-white text-navy border-slate-200 hover:border-orange"}`}>
                  {d.label}
                </button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={!data} className="ml-auto" data-testid="gsc-export-btn">
              <Download size={14} className="mr-1.5" /> Export CSV
            </Button>
          </div>

          {loading ? (
            <div className="py-16 text-center text-slate-500" data-testid="gsc-loading">
              <Loader2 className="animate-spin mx-auto mb-3" /> Pulling data from Google…
            </div>
          ) : !data ? (
            <p className="py-10 text-center text-slate-500">No data loaded.</p>
          ) : (
            <>
              <div className="mt-5 grid sm:grid-cols-3 gap-4" data-testid="gsc-totals">
                {[
                  { label: "Clicks", cur: num(data.totals.clicks), prev: data.previous_totals.clicks, now: data.totals.clicks },
                  { label: "Impressions", cur: num(data.totals.impressions), prev: data.previous_totals.impressions, now: data.totals.impressions },
                  { label: "CTR", cur: pct(data.totals.ctr), prev: null, now: null },
                ].map((c) => (
                  <div key={c.label} className="rounded-lg border border-slate-100 bg-slate-50 p-4">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{c.label}</p>
                    <p className="text-2xl font-display font-extrabold text-navy mt-1">{c.cur}</p>
                    {c.prev !== null && <p className="text-xs text-slate-500 mt-1">vs {num(c.prev)} previous period · <Delta value={Math.round(c.now - c.prev)} /></p>}
                  </div>
                ))}
              </div>

              <p className="text-xs text-slate-500 mt-4">
                {data.period.start} → {data.period.end} · compared with {data.previous_period.start} → {data.previous_period.end} · {data.row_count} rows. {data.note}
              </p>

              <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter…" className="mt-4 max-w-sm" data-testid="gsc-filter" />

              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm" data-testid="gsc-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                      <th className="py-2 pr-4">{DIMENSIONS.find((d) => d.key === dimension)?.label || dimension}</th>
                      <th className="py-2 px-3 text-right">Clicks</th>
                      <th className="py-2 px-3 text-right">Δ</th>
                      <th className="py-2 px-3 text-right">Impressions</th>
                      <th className="py-2 px-3 text-right">Δ</th>
                      <th className="py-2 px-3 text-right">CTR</th>
                      <th className="py-2 pl-3 text-right">Avg position</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 300).map((r) => (
                      <tr key={r.key} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 pr-4 font-medium text-navy break-all max-w-md">{r.key.replace(/^https?:\/\/[^/]+/, "") || r.key}</td>
                        <td className="py-2 px-3 text-right">{num(r.clicks)}</td>
                        <td className="py-2 px-3 text-right"><Delta value={Math.round(r.delta_clicks)} /></td>
                        <td className="py-2 px-3 text-right">{num(r.impressions)}</td>
                        <td className="py-2 px-3 text-right"><Delta value={Math.round(r.delta_impressions)} /></td>
                        <td className="py-2 px-3 text-right">{pct(r.ctr)}</td>
                        <td className="py-2 pl-3 text-right">{r.position.toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rows.length > 300 && <p className="text-xs text-slate-500 mt-3">Showing the top 300 of {rows.length} rows — use Export CSV for the full set.</p>}
                {rows.length === 0 && <p className="py-10 text-center text-slate-500" data-testid="gsc-empty">No rows for this period.</p>}
              </div>
            </>
          )}
        </div>
      )}

      <UrlInspector enabled={!!(status?.connected && status?.property)} />
    </div>
  );
}
